import { BaseTrack, TrackConfig, TrackData } from '../base/base-track';

export const UNPAIRED_COLOR = '#FFFFFF';

// Palette cycled across stems so each helix gets a distinct colour.
// Mid-saturation shades keep the residue letters legible.
export const STEM_PALETTE = [
  '#5DADE2',  // blue
  '#F39C12',  // orange
  '#58D68D',  // green
  '#AF7AC5',  // purple
  '#EC7063',  // coral
  '#1ABC9C',  // teal
  '#F7DC6F',  // yellow
  '#EB984E',  // amber
  '#48C9B0',  // aquamarine
  '#C39BD3',  // lavender
  '#76D7C4',  // mint
  '#F1948A',  // salmon
];

// WUSS bracket pairs: open char → close char
const OPEN_TO_CLOSE: Record<string, string> = { '<': '>', '(': ')', '[': ']', '{': '}' };

/**
 * Parse a WUSS SS_cons string and return a per-position colour array.
 *
 * Stem definition: for each bracket type independently, a stem spans from
 * the moment the stack goes empty→non-empty (first open bracket) until it
 * returns to empty (last matching close bracket).  This means:
 *   <<<___<<<____>>>>>>   → ONE stem  (stack never empties mid-way)
 *   <<<___<<<____>>>>>><<<____>>>  → TWO stems  (stack empties between them)
 *   <<__<<___<_________>_>>>>     → ONE stem
 */
export function buildStemColorMap(consensus: string): string[] {
  const OPEN_CHARS = ['<', '(', '[', '{'] as const;

  const allStemGroups: Array<Array<{ left: number; right: number }>> = [];

  for (const openChar of OPEN_CHARS) {
    const closeChar = OPEN_TO_CLOSE[openChar];
    const stack: number[] = [];
    let currentGroup: Array<{ left: number; right: number }> = [];

    for (let i = 0; i < consensus.length; i++) {
      const c = consensus[i];
      if (c === openChar) {
        if (stack.length === 0) {
          // Stack was empty — this is the start of a new stem
          currentGroup = [];
        }
        stack.push(i);
      } else if (c === closeChar) {
        if (stack.length > 0) {
          const left = stack.pop()!;
          currentGroup.push({ left, right: i });
          if (stack.length === 0) {
            // Stack drained — stem is complete
            allStemGroups.push([...currentGroup]);
            currentGroup = [];
          }
        }
      }
    }
  }

  if (allStemGroups.length === 0) {
    return new Array(consensus.length).fill(UNPAIRED_COLOR);
  }

  // Order stems left-to-right for consistent colour assignment
  allStemGroups.sort((a, b) =>
    Math.min(...a.map(p => p.left)) - Math.min(...b.map(p => p.left))
  );

  const positionColors = new Array<string>(consensus.length).fill(UNPAIRED_COLOR);
  for (let s = 0; s < allStemGroups.length; s++) {
    const color = STEM_PALETTE[s % STEM_PALETTE.length];
    for (const { left, right } of allStemGroups[s]) {
      positionColors[left]  = color;
      positionColors[right] = color;
    }
  }

  return positionColors;
}

export class MSATrack extends BaseTrack {

  constructor(config: TrackConfig, data: TrackData) {
    super('msa', config, data);
  }

  createHTML(): string {
    if (!this.hasRequiredData()) {
      return '';
    }

    // clustal2 is the fallback; overridden by injectStructureColorScheme when
    // notation.consensus is present.
    return `
      <nightingale-msa
        id="msa-track"
        style="width: 100%; display: block;"
        height="${this.config.height}"
        color-scheme="clustal2"
        length="${this.config.sequenceLength}"
        display-start="${this.config.displayStart}"
        display-end="${this.config.displayEnd}"
        label-width="${this.config.labelWidth}"
        label-font-size="16"
        label-font-weight="600"
        use-ctrl-to-zoom>
      </nightingale-msa>
    `;
  }

  setup(container: HTMLElement): void {
    this.element = container.querySelector('#msa-track') as HTMLElement;

    if (!this.element) {
      console.error('MSA track element not found');
      return;
    }

    this.setupMSATrack();
  }

  update(config?: Partial<TrackConfig>, data?: Partial<TrackData>): void {
    if (config) {
      this.config = { ...this.config, ...config };
    }
    if (data) {
      this.data = { ...this.data, ...data };
    }

    if (this.element) {
      this.setupMSATrack();
    }
  }

  destroy(): void {
    this.element = undefined;
  }


  private setupMSATrack(): void {
    if (!this.element) return;

    const nightingaleAttributes = this.getNightingaleAttributes();
    this.applyAttributes(this.element, {
      ...nightingaleAttributes,
      'label-width': this.config.labelWidth.toString(),
      'label-font-size': '16',
      'label-font-weight': '600',
      'use-ctrl-to-zoom': 'true'
    });

    this.element.style.display = 'block';
    this.element.style.width = '100%';

    this.injectStructureColorScheme();
  }

  /**
   * Replace nightingale-msa's internal colorSchemeManager with a per-stem
   * colour scheme derived from notation.consensus (WUSS SS_cons notation).
   *
   * Each detected stem (group of consecutive base pairs) gets a distinct
   * colour from STEM_PALETTE so that paired residues belonging to the same
   * helix share a colour while different helices are visually distinct.
   * Unpaired positions (. : _ - ,) remain white.
   */
  private injectStructureColorScheme(): void {
    const consensus = this.data.notation?.consensus;
    if (!consensus || !this.element) return;

    const positionColors = buildStemColorMap(consensus);

    const customColorScheme = {
      scheme: { type: 'static', map: {}, getColor: () => UNPAIRED_COLOR },
      updateConservation: () => { /* no-op */ },
      getColor: (_element: string, position: number): string =>
        positionColors[position] ?? UNPAIRED_COLOR,
    };

    // nightingale-msa is a Lit element; msa-sequence-viewer lives in its shadow root
    const msaElement = this.element as any;
    const renderRoot = msaElement.renderRoot as ShadowRoot | undefined;
    if (!renderRoot) return;

    const sequenceViewer = renderRoot.querySelector('msa-sequence-viewer') as any;
    if (!sequenceViewer) return;

    sequenceViewer.colorSchemeManager = customColorScheme;

    // Invalidate caches so the next draw uses the new colours
    sequenceViewer.residueTileCache?.invalidate();
    sequenceViewer.tileCache?.invalidate();
  }
}
